import React, { Suspense, useMemo } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { mapOffersToProductPrice } from '@/core/utils/productSkuOfferConnection'

import { useProductPageUrlParams } from '../../hooks'
import { ProductSkuPriceLoading } from './ProductSkuPriceLoading'
import type { ProductSkuPrice_Product$key } from './__generated__/ProductSkuPrice_Product.graphql'
import { PriceInfo } from './PriceInfo'

const productFragment = graphql`
  fragment ProductSkuPrice_Product on Product {
    skus {
      edges {
        node {
          id
          offers(addresses: $addresses) {
            ...productSkuOfferConnection_ProductSkuOfferConnection
          }
        }
      }
    }
  }
`

type ProductSkuPriceContentProps = {
  productRef: ProductSkuPrice_Product$key
}

const ProductSkuPriceContent = ({
  productRef,
}: ProductSkuPriceContentProps) => {
  const { selectedSkuId } = useProductPageUrlParams()
  const product = useSuspenseFragment(productFragment, productRef)

  const selectedSku = useMemo(() => {
    return product.skus.edges.find(
      (skuEdge) => skuEdge.node.id === selectedSkuId
    )
  }, [product.skus.edges, selectedSkuId])

  if (!selectedSku?.node.offers) {
    return null
  }

  const [bestOffer] = mapOffersToProductPrice(selectedSku.node.offers)

  return <PriceInfo offer={bestOffer} />
}

export type ProductSkuPriceProps = {
  productRef: ProductSkuPrice_Product$key
}

export const ProductSkuPrice = (props: ProductSkuPriceProps) => (
  <div data-testid="product-sku-price">
    <Suspense fallback={<ProductSkuPriceLoading />}>
      <ProductSkuPriceContent {...props} />
    </Suspense>
  </div>
)
